import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { getCohortBuilderPath } from '@verantos-dev/core-constants';
import {
  useCreateCohortMutation,
  usePostCohortVersionMutation,
} from '@verantos-dev/data-access-cohorts';
import { type CohortVersion } from '@verantos-dev/types-cohorts';
import { type ObjectResponse } from '@verantos-dev/types-common';
import { type ProjectDetail } from '@verantos-dev/types-projects';

export const useCohortCreation = (
  projectId: string | undefined,
  projectData: ObjectResponse<ProjectDetail> | undefined,
  sourceCohortVersion: ObjectResponse<CohortVersion> | undefined,
  newCohortName: string,
  itemName: string | null
) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [createCohort, { isLoading: isCreatingCohort }] = useCreateCohortMutation();
  const [postCohortVersion, { isLoading: isCreatingVersion }] = usePostCohortVersionMutation();

  const handleSubmit = useCallback(async () => {
    try {
      // Create new cohort
      const newCohort = await createCohort({
        projectId: projectId || '',
        data: {
          cohortName: newCohortName,
          cohortDescription: `Copy of ${itemName}`,
          datasetReleaseId: projectData?.data?.datasetReleaseId || '',
        },
      }).unwrap();

      // Copy cohort version details
      const newVersion = await postCohortVersion({
        projectId: projectId || '',
        profileId: newCohort.data.cohortProfileId,
        data: {
          dslVersion: sourceCohortVersion?.data?.dslVersion || '',
          dslValue: sourceCohortVersion?.data?.dslValue || '',
          sqlValue: sourceCohortVersion?.data?.sqlValue || '',
        },
      }).unwrap();

      // Navigate to the new cohort
      navigate(
        `${getCohortBuilderPath(
          projectId || '',
          newCohort.data.cohortProfileId,
          newVersion.data.cohortVersionId
        )}?pageTab=events`
      );

      return {
        success: true,
        successMessage: intl.formatMessage(
          { id: 'cohortsList.copyCohortModal.success.text' },
          { cohortName: newCohort.data.cohortName }
        ),
      };
    } catch (error) {
      console.error('Error copying cohort:', error);
      throw error;
    }
  }, [
    projectId,
    newCohortName,
    itemName,
    createCohort,
    postCohortVersion,
    navigate,
    intl,
    projectData,
    sourceCohortVersion,
  ]);

  return {
    handleSubmit,
    isCreatingCohort,
    isCreatingVersion,
  };
};
